import {
  ArrowDownTrayIcon,
  ArrowsPointingInIcon,
  ArrowsPointingOutIcon,
  QuestionMarkCircleIcon,
} from '@heroicons/react/20/solid';
import { Button } from '../../catalyst/button';
import type { ViewMode } from './viewUrl';
import { ViewModeToggle } from './ViewModeToggle';

const TOOLBAR_BUTTON = 'px-2 py-1 text-xs/5 sm:px-2 sm:py-1 sm:text-xs/5';

/** The full screen state of the graph area, and its toggle; the `f` key does the same. */
export type FullscreenControl = { active: boolean; onToggle: () => void };

/**
 * The graph area's own controls, top right: Graph or List, the hint toggle, the CSV export, Centre on focus, and full
 * screen. The list draws them too, without the canvas's hint and Centre on focus.
 */
export function CanvasToolbar({
  mode,
  onMode,
  helpShown = false,
  onToggleHelp,
  onCentre,
  onExportCsv,
  fullscreen,
}: {
  mode: ViewMode;
  onMode: (mode: ViewMode) => void;
  helpShown?: boolean;
  /** Absent where there is no hint (the list): the button is hidden. */
  onToggleHelp?: () => void;
  /** Pans to the root at zoom 1; the `c` key does the same. Absent in the list: the button is hidden. */
  onCentre?: () => void;
  /** Downloads the visible relationships as a CSV file. */
  onExportCsv: () => void;
  /** Absent where the page may not go full screen: the button is hidden. */
  fullscreen?: FullscreenControl;
}) {
  const FullscreenIcon = fullscreen?.active ? ArrowsPointingInIcon : ArrowsPointingOutIcon;
  return (
    <div className="absolute top-3 right-3 z-10 flex gap-2">
      <ViewModeToggle mode={mode} onChange={onMode} />
      {/* The outline buttons are see-through; the wrappers keep the edges behind them from showing. */}
      {onToggleHelp && (
        <div className="rounded-lg bg-white shadow-sm dark:bg-zinc-800 dark:shadow-zinc-950/30">
          <Button
            outline
            buttonClassName={TOOLBAR_BUTTON}
            aria-label={helpShown ? 'Hide the graph hint' : 'Show the graph hint'}
            aria-expanded={helpShown}
            onClick={onToggleHelp}
          >
            <QuestionMarkCircleIcon data-slot="icon" />
          </Button>
        </div>
      )}
      <div className="rounded-lg bg-white shadow-sm dark:bg-zinc-800 dark:shadow-zinc-950/30">
        <Button
          plain
          buttonClassName={TOOLBAR_BUTTON}
          title="Export the visible relationships as CSV"
          aria-label="Export the visible relationships as CSV"
          onClick={onExportCsv}
        >
          <ArrowDownTrayIcon data-slot="icon" />
          CSV
        </Button>
      </div>
      {onCentre && (
        <div className="rounded-lg bg-white shadow-sm dark:bg-zinc-800 dark:shadow-zinc-950/30">
          <Button outline buttonClassName={TOOLBAR_BUTTON} onClick={onCentre}>
            Centre on focus
          </Button>
        </div>
      )}
      {fullscreen && (
        <div className="rounded-lg bg-white shadow-sm dark:bg-zinc-800 dark:shadow-zinc-950/30">
          <Button
            outline
            buttonClassName={TOOLBAR_BUTTON}
            aria-label={fullscreen.active ? 'Exit full screen' : 'Full screen'}
            onClick={fullscreen.onToggle}
          >
            <FullscreenIcon data-slot="icon" />
          </Button>
        </div>
      )}
    </div>
  );
}
